import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate } from 'gatsby'
import styled, { css } from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import startCase from 'lodash/startCase'
import kebabCase from 'lodash/kebabCase'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import dayjs from 'dayjs'

import theme from 'theme'
import media, { pixels } from 'media'

import useAnvilUser from 'hooks/useAnvilUser'
import useAnalytics from 'lib/analytics/useAnalytics'
import { EVENT_FORM_SUBMITTED } from 'lib/analytics/events'
import { H1, H2, H3 } from 'lib/ui/primitives'

import ContactAnvil from 'components/Signup/ContactAnvil'
import Helmet from 'components/page/Helmet'
import Layout from 'components/page/layouts/GatsbyLayout'
import Button, { CLASS_NAME as CLASS_NAME_BUTTON } from 'components/Button'
import SidebarSection, {
  SidebarContainer,
  ChildrenContainer,
} from 'components/page/Section/SidebarSection'
import Sidebar, {
  CLASS_NAME as CLASS_NAME_SIDEBAR,
} from 'components/page/Section/SidebarSection/FormSidebar'
import UniversalLink from 'components/UniversalLink'
import OpinionatedSplitContainer from 'components/NewSplitContainer/OpinionatedSplitContainer'
import EmbedForm from 'components/EmbedForm'
import NewFormSummary, {
  Container,
} from 'components/summaryCards/NewFormSummary'
import HubspotForm from 'components/hubspot/HubspotForm'

import { renderIcon as renderIndustryIcon } from '@anvilco-pkg/ui/components/Icon/industries'
import EmbedOnlyModal from '@anvilco-pkg/ui/components/Modal/EmbedOnlyModal'
import Modal from '@anvilco-pkg/ui/components/Modal'
import Carousel from '@anvilco-pkg/ui/components/Carousel'
import Markdown from '@anvilco-pkg/ui/components/Markdown'

import { default as formTags } from 'lib/content/forms/tags'

import renderNavbar from 'helpers/renderNavbar'
import { APP_URL, getSocialCoverURL } from 'helpers'
import { getPagePath } from 'helpers/path'

import { FORMS_SLUG, CLASS_STYLE_AS_LINK } from 'consts'

import Step1Asset from './Step1Asset'
import step2Webp from './step2.webp'
import Step3Asset from './Step3Asset'
import { SolutionsCTA } from '../components'

// TODO: CANDYCODE migrate to next.js

const possibleFormTags = formTags.default
const FORM_SCROLL_NAME = 'contact-sales'
const TITLE_CHAR_LIMIT = 60

const StyledModal = styled(Modal)`
  font-size: 16px;
  border-top: 3px solid ${theme.colors.greens[30]};

  .ant-modal-content {
    min-height: unset !important;
  }
`

const ModalSubtitle = styled.div`
  font-size: ${theme.fontSizes[50]}px;

  a {
    text-decoration: underline;
    font-weight: ${theme.fontWeights.bold};
    color: ${theme.colors.black};
  }
`

const SummaryContainer = styled(Box).attrs({
  px: [10],
})`
  display: inline-flex !important;
  flex-direction: column;
  height: inherit;

  ${Container} {
    flex: 1;
  }
`

const StyledCarousel = styled(Carousel)`
  margin-top: 90px;

  .slick-track {
    display: flex !important;
  }

  .slick-slide {
    height: inherit !important;
    display: flex !important;
    justify-content: center;

    & > div {
      width: 100%;
      height: inherit !important;
      display: flex !important;
    }

    ${Container} {
      height: inherit;
    }
  }

  ${media.tablet`
    margin-top: 40px;
  `}
`

const EmbeddedWebform = styled(EmbedForm)`
  width: 400px;
`

const StyledSidebarSection = styled(SidebarSection)`
  ${ChildrenContainer} {
    width: 100%;
    padding-left: 0;
  }

  ${SidebarContainer} {
    flex-direction: column;
  }

  ${media.giant`
   .${CLASS_NAME_SIDEBAR} {
      min-width: unset;
      max-width: 250px;
    }

   ${SidebarContainer} {
      flex-direction: row;
    }

    ${ChildrenContainer} {
      padding-left: 40px;
    }
  `}

  ${media.beyondGiant`
    ${ChildrenContainer} {
      padding-left: 50px;
    }
  `}
`

const MainContentContainer = styled(Box)``

const HeaderContainer = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'row'],
  justifyContent: ['flex-start', 'flex-start', 'space-between'],
})``

const HeaderTitle = styled(Box).attrs({
  maxWidth: ['100%', '100%', '75%'],
  pr: [0, 0, 50],
})``

const Title = styled(H1).attrs({
  mt: [6],
  mb: ['4px'],
})`
  font-weight: ${theme.fontWeights.bold};
`

const UpdatedDate = styled.p`
  font-size: 14px;
  color: ${theme.colors.blacks[50]};
`

const HeaderAction = styled(Flex).attrs({
  flexDirection: ['column'],
  alignItems: 'center',
  mt: [0, 30, 0],
})`
  min-width: auto;
  width: fit-content;

  display: none;

  ${media.tablet`
    display: flex;
  `}
`

const HeaderActionSubtitle = styled(Box).attrs({
  as: 'span',
  textAlign: ['left', 'left', 'center'],
})`
  display: inline-block;
  color: ${theme.colors.blacks[70]};
  margin-top: 4px;
`

const AnvilTag = styled.div.withConfig({
  shouldForwardProp: (prop) => ['isPdfTemplate'].indexOf(prop) === -1,
})`
  ${({ isPdfTemplate }) =>
    isPdfTemplate
      ? css`
          background: ${theme.colors.blues[10]};
          color: ${theme.colors.blues[50]};
        `
      : css`
          background: ${theme.colors.greens[10]};
          color: ${theme.colors.greens[50]};
        `}
  font-weight: 500;
  padding: 2px 10px;
  /* check mobile safari for this one */
  width: fit-content;
`

const ImageSection = styled(Box).attrs({
  mt: [24],
  width: ['100vw', 1],
  ml: [-20, 0],
})``

const MOBILE_PDF_ICON_HEIGHT = 34
const TABLET_PDF_ICON_HEIGHT = 50
const PDF_ICON_HEIGHT = 74

const PDFContainer = styled(Flex).attrs({
  justifyContent: 'center',
  width: [1],
  pt: [MOBILE_PDF_ICON_HEIGHT, TABLET_PDF_ICON_HEIGHT, PDF_ICON_HEIGHT],
  px: [MOBILE_PDF_ICON_HEIGHT, TABLET_PDF_ICON_HEIGHT, PDF_ICON_HEIGHT],
})`
  overflow: hidden;
  background: ${theme.colors.blues[20]};
`

const PositionContainer = styled.div`
  position: relative;
`

const ImageContainer = styled.div`
  width: 264px;
  height: 342px;
  margin-bottom: -95px;

  ${media.tablet`
    width: 343px;
    height: 444px;
    margin-bottom: -130px;
  `}

  ${media.desktop`
    width: 553px;
    height: 715px;
    margin-bottom: -210px;
  `}
`

const PDFIcon = styled(Flex)
  .attrs({
    justifyContent: 'center',
    alignItems: 'center',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isPdfTemplate'].indexOf(prop) === -1,
  })`
  height: ${MOBILE_PDF_ICON_HEIGHT}px;
  width: ${MOBILE_PDF_ICON_HEIGHT}px;
  position: absolute;
  top: -${MOBILE_PDF_ICON_HEIGHT}px;
  right: -${MOBILE_PDF_ICON_HEIGHT}px;
  background: ${theme.colors.blues[30]};
 
  svg {
    width: 20px;
    height: auto;
  }

  ${media.tablet`
    height: ${TABLET_PDF_ICON_HEIGHT}px;
    width: ${TABLET_PDF_ICON_HEIGHT}px;
    top: -${TABLET_PDF_ICON_HEIGHT}px;
    right: -${TABLET_PDF_ICON_HEIGHT}px;

    svg {
      width: 28px;
    }
  `}

  ${media.desktop`
    height: ${PDF_ICON_HEIGHT}px;
    width: ${PDF_ICON_HEIGHT}px;
    top: -${PDF_ICON_HEIGHT}px;
    right: -${PDF_ICON_HEIGHT}px;

    svg {
      width: 35px;
    }
  `}
`

const ActionContainer = styled(Flex).attrs({
  alignItems: 'stretch',
  flexDirection: ['column', 'column', 'row'],
})``

const ActionBase = styled(Flex).attrs({
  px: [30],
  py: [30],
  flexDirection: ['column'],
  justifyContent: 'space-between',
})``

const PDFDescription = styled(ActionBase)`
  background: ${theme.colors.blacks[70]};
  color: ${theme.colors.white};
`

const PDFAction = styled(ActionBase).attrs({})`
  background: ${theme.colors.blacks[10]};
  font-size: 15px;
`

const DescriptionTitle = styled.div`
  font-size: 18px;
  font-weight: ${theme.fontWeights.bold};
  margin-bottom: 6px;
`

const ActionTitle = styled.div`
  font-weight: ${theme.fontWeights.bold};
`

const CopyBase = styled.div`
  font-size: 15px;
`

const DescriptionCopy = styled(CopyBase)`
  a {
    color: ${theme.colors.greens[30]} !important;

    :hover {
      color: ${theme.colors.greens[20]} !important;
    }
  }
`

const ActionCopy = styled(CopyBase)``

const BackToLibraryLink = styled(UniversalLink)`
  display: inline-block;
  margin-top: 24px;
  font-size: 15px;
  color: ${theme.colors.blacks[50]} !important;
  text-decoration: none !important;
  font-weight: ${theme.fontWeights.thin} !important;

  :hover {
    color: ${theme.colors.black} !important;
    font-weight: ${theme.fontWeights.bold} !important;
  }
`

const HowToSection = styled.main``

const HowToHeader = styled(Box).attrs({
  m: ['30px 0', '40px auto', '60px auto'],
  textAlign: ['left', 'center'],
})`
  max-width: 500px;
`

const HowToTitle = styled(H2)`
  margin-bottom: 16px;
  font-weight: ${theme.fontWeights.bold};
`

const HowToSubtitle = styled.p`
  color: ${theme.colors.blacks[70]};
`

const SplitTextContainer = styled.div``

const SplitTextTitle = styled(H3)`
  margin-bottom: 28px;
  font-weight: ${theme.fontWeights.bold};
`

const SplitTextBlock = styled.div`
  margin-bottom: 20px;
`

const OtherFormsContainer = styled(Box).attrs({
  pt: [30, 40, 75],
})``

const OtherFormsHeader = styled(Box).attrs({
  textAlign: ['left', 'center'],
})`
  p {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`

const SectionBreakBase = styled(Box)`
  background: ${theme.colors.blues[10]};
`

const TalkToSalesContainer = styled(SectionBreakBase).attrs({
  mt: [30, 40, 40, 80],
  py: 50,
  px: [20, 20, 150],
  width: ['100vw', 1],
  ml: [-20, 0],
  textAlign: ['left', 'center'],
})`
  h2 {
    font-weight: ${theme.fontWeights.bold};
  }

  p {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .${CLASS_NAME_BUTTON} {
    width: 100%;
  }

  ${media.tablet`
    .${CLASS_NAME_BUTTON} {
      width: unset;
    }
  `}
`

const getSolutionsContainerAttrs = ({ noCarouselNodes }) => ({
  mt: noCarouselNodes ? [80] : [110, 110, 120, 90],
})

const StyledSolutionsCTA = styled(SolutionsCTA)
  .attrs(getSolutionsContainerAttrs)
  .withConfig({
    shouldForwardProp: (prop) => ['noCarouselNodes'].indexOf(prop) === -1,
  })`
  
`

const getFlexContainerAttrs = ({ justifyContent, pl, pr }) => ({
  justifyContent: ['center', 'center', justifyContent || 'flex-end'],
  pl,
  pr,
  mb: [50, 50, 0],
})
const FlexContainer = styled(Flex).attrs(getFlexContainerAttrs)`
  width: 100%;

  ${media.tablet`
    & > * {
      width: 80%;
      height: auto;

      :not(svg) {
        width: 60%;
      }
    }
  `}

  ${media.desktop`
    & > * {
      :is(svg) {
        width: 100%;
      }
      
      :not(svg) {
        width: 80%;
      }
    }
  `}
`

const Step2Webp = styled.img.attrs({ src: step2Webp })`
  display: block;
  max-width: 100%;
`

function FormTemplate ({
  // locationState --> how to use here?
  location: { pathname, state: locationState },
  data: {
    site: {
      siteMetadata: { siteUrl },
    },
    form: {
      frontmatter: {
        appSlug,
        keywords,
        slug,
        summary,
        tags,
        title,
        lastUpdated,
        type,
        workflowEid,
        pdfTemplateEid,
      },
      fields: { dateLastModified } = {},
      pdfScreenshot,
    },
    relatedArticles: { nodes: relatedNodes },
    allTagArticles: { nodes: allTagArticleNodes },
    // relatedForms: { nodes: relatedForms },
  }, // this prop will be injected by the GraphQL query below.
  pageContext: {
    newHtmlAst,
    relatedNodesCurrentTags,
    canonicalTag,
    selectedTag,
  },
}) {
  if (title.length >= TITLE_CHAR_LIMIT) {
    throw new Error(
      `Form title is too long. Please shorten to ${TITLE_CHAR_LIMIT} or fewer characters.`
    )
  }

  // TODO: throw error if pdfTemplateEid is not supplied

  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false)
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false)

  const { eid } = useAnvilUser() || {}
  const isUserLoggedInToAnvil = !!eid

  const formSubmitHandler = useAnalytics({
    eventName: EVENT_FORM_SUBMITTED,
  })

  // set cookie to check if user is logged in to Anvil after mounting
  // to avoid hydration issues
  useEffect(() => {
    window?.addEventListener('message', ({ origin, data: eventObject }) => {
      if (origin !== APP_URL) return
      if (eventObject.action === 'weldComplete') {
        formSubmitHandler({
          // NOTE: marketers/people who use this data will likely
          // be more comfortable with the name of the workflow instead of
          // eid... what about the submission name?
          workflow_eid: eventObject.weldEid,
          workflow_submission_eid: eventObject.weldDataEid,
          action: 'Submitted',
          // TODO: send location_on_page... but we usually get this
          // from SectionContext, and the section we're in is not
          // set up to use SectionContext (it's just a styled.main)
        })
      }
    })
  }, [])

  const copyPDFTemplateUrl = `${APP_URL}/copy-pdf-template/${pdfTemplateEid}`
  const copyWorkflowUrl = `${APP_URL}/copy-workflow/${workflowEid}`
  const downloadEmptyPDFTemplateUrl = `${APP_URL}/download/cast/${pdfTemplateEid}.pdf`
  const exampleFilledPDFTemplateUrl = `${APP_URL}/api/v1/fill-form-demo/${pdfTemplateEid}.pdf`
  const webformUrl = `${APP_URL}/form/free/${appSlug || slug}`
  // const webformUrl = `${APP_URL}/form/demo/sample-workflow` // used for local testing
  const shouldRenderPdfTemplateView = pdfTemplateEid && !workflowEid
  // TODO: use this to give enhanced SERP view via JSON-LD
  const dateLastUpdated = lastUpdated
    ? dayjs(lastUpdated)
    : dayjs(dateLastModified)

  const renderHeader = () => {
    return (
      <HeaderContainer>
        <HeaderTitle>
          <AnvilTag isPdfTemplate={shouldRenderPdfTemplateView}>
            {shouldRenderPdfTemplateView
              ? 'PDF template'
              : 'Workflow: PDF + Webform'}
          </AnvilTag>
          <Title>{title}</Title>
          <UpdatedDate>
            Updated {dateLastUpdated.format('MMM DD, YYYY')}
          </UpdatedDate>
          <Markdown>{summary}</Markdown>
        </HeaderTitle>
        <HeaderAction>
          <Button
            type="black"
            chevron={{ direction: 'right', position: 'inner' }}
            size="large"
            onClick={() => {
              if (!isUserLoggedInToAnvil) setIsDownloadModalOpen(true)
            }}
            to={
              isUserLoggedInToAnvil
                ? shouldRenderPdfTemplateView
                  ? copyPDFTemplateUrl
                  : copyWorkflowUrl
                : undefined
            }
          >
            {isUserLoggedInToAnvil ? 'Add this template' : 'Download this form'}
          </Button>
          <HeaderActionSubtitle>
            {isUserLoggedInToAnvil ? (
              'to your account'
            ) : (
              <>
                or{' '}
                <UniversalLink to={FORM_SCROLL_NAME}>
                  explore your use case with our team
                </UniversalLink>
              </>
            )}
          </HeaderActionSubtitle>
        </HeaderAction>
        <StyledModal
          title={`Download form ${title}`}
          isClosable
          description={
            <ModalSubtitle>
              If you need to use this form as part of a workflow you're
              building,{' '}
              <UniversalLink
                to={FORM_SCROLL_NAME}
                onClick={() => setIsDownloadModalOpen(false)}
              >
                our team can help
              </UniversalLink>
              .
            </ModalSubtitle>
          }
          visible={isDownloadModalOpen}
          onChangeVisible={setIsDownloadModalOpen}
        >
          <HubspotForm
            formName="Download PDF"
            prefilledFields={{
              formLibraryPageTitle: title,
            }}
            onSubmit={() => {
              const aElement = document.createElement('a')
              aElement.href = downloadEmptyPDFTemplateUrl
              aElement.setAttribute('target', '_blank')
              aElement.click()
            }}
          />
        </StyledModal>
      </HeaderContainer>
    )
  }

  const renderPDFImageSection = () => {
    return (
      <ImageSection>
        <PDFContainer>
          <PositionContainer>
            <ImageContainer>
              <GatsbyImage
                image={getImage(
                  pdfScreenshot?.childImageSharp?.gatsbyImageData
                )}
                alt={`${title} screenshot`}
              />
            </ImageContainer>
            <PDFIcon isPdfTemplate={shouldRenderPdfTemplateView}>
              {renderIndustryIcon(
                `Icon${startCase(selectedTag)?.replaceAll(' ', '')}`
              )}
            </PDFIcon>
          </PositionContainer>
        </PDFContainer>
        <ActionContainer>
          <PDFDescription>
            <DescriptionTitle>Using this at your organization</DescriptionTitle>
            <DescriptionCopy>
              If you're in a product management, development, or people ops role
              & want to simplify your PDF-filling & E-sign processes,{' '}
              <UniversalLink to={FORM_SCROLL_NAME}>
                talk to our team
              </UniversalLink>
              .
            </DescriptionCopy>
          </PDFDescription>
          <PDFAction>
            <ActionTitle>
              {shouldRenderPdfTemplateView
                ? `See the filled PDF example`
                : `For individuals:`}
            </ActionTitle>
            <ActionCopy>
              {shouldRenderPdfTemplateView ? (
                <>
                  Curious to see what your final PDF will look like?{' '}
                  <UniversalLink to={exampleFilledPDFTemplateUrl}>
                    See an example here
                  </UniversalLink>
                  .
                </>
              ) : (
                <>
                  Need to fill out {title} for yourself? We've got you!{' '}
                  <UniversalLink to={webformUrl}>
                    Fill out this Webform
                  </UniversalLink>
                  , get a completed PDF, and get it signed.
                </>
              )}
            </ActionCopy>
          </PDFAction>
        </ActionContainer>
      </ImageSection>
    )
  }

  const renderHowTo = () => {
    const LinkOrButton = shouldRenderPdfTemplateView
      ? UniversalLink
      : (props) => <Button {...props} className={CLASS_STYLE_AS_LINK} />

    return (
      <HowToSection>
        <HowToHeader>
          <HowToTitle>How to fill {title}, your way.</HowToTitle>
          <HowToSubtitle>
            The steps below are convenient starting point for most businesses.
            You can customize anything to fit your unique needs.
          </HowToSubtitle>
        </HowToHeader>
        <OpinionatedSplitContainer
          mobileStartWithAsset
          textColumn={{
            width: [1, 1, 1, 0.42],
            itemsToRender: [
              (props) => (
                <SplitTextContainer>
                  <SplitTextTitle>Fill {title} with your data</SplitTextTitle>
                  <SplitTextBlock>
                    <strong>Option 1: over API</strong>
                    <div>
                      If you already have the data, you can use Anvil's PDF
                      filling endpoint to fill the {title} PDF template from any
                      application. Send your data as JSON and receive the filled
                      PDF.
                    </div>
                  </SplitTextBlock>
                  <SplitTextBlock>
                    <strong>Option 2: from Webform</strong>
                    <div>
                      Generate filled PDF documents with user-submitted data
                      collected through the Webform.
                    </div>
                  </SplitTextBlock>
                  <UniversalLink
                    to={
                      shouldRenderPdfTemplateView
                        ? copyPDFTemplateUrl
                        : copyWorkflowUrl
                    }
                  >
                    {isUserLoggedInToAnvil
                      ? 'Add this template to your account'
                      : 'Sign up to use this template'}
                  </UniversalLink>
                </SplitTextContainer>
              ),
              (props) => (
                <SplitTextContainer>
                  <SplitTextTitle>
                    {shouldRenderPdfTemplateView
                      ? 'Need to collect data first? Create a Webform.'
                      : 'Need to collect data first? Use our pre-made Webform.'}
                  </SplitTextTitle>
                  <SplitTextBlock>
                    {shouldRenderPdfTemplateView
                      ? 'Use our drag-and-drop Webform tool to collect data from your users. Easily link Webform fields to those on the PDF and use the template as many times as you need.'
                      : "Use our intuitive production-ready Webform to collect data from your users. We already set up all the questions so you don't have to."}
                  </SplitTextBlock>
                  <SplitTextBlock>
                    Embed it into you product and customize styling to create a
                    seamless on-brand experience for your clients or end users.
                  </SplitTextBlock>
                  <LinkOrButton
                    onClick={(e) => {
                      if (!shouldRenderPdfTemplateView) {
                        e.preventDefault()
                        setIsEmbedModalOpen(true)
                      }
                    }}
                    to={
                      shouldRenderPdfTemplateView ? FORM_SCROLL_NAME : undefined
                    }
                  >
                    {shouldRenderPdfTemplateView
                      ? 'Explore your use case'
                      : 'Preview this Webform'}
                  </LinkOrButton>
                </SplitTextContainer>
              ),
              (props) => (
                <SplitTextContainer>
                  <SplitTextTitle>
                    Collect e-signatures in the same workflow
                  </SplitTextTitle>
                  <SplitTextBlock>
                    No need to use 3rd party E-sign services - Anvil makes it
                    easy to get your forms signed in the same workflow.
                  </SplitTextBlock>
                </SplitTextContainer>
              ),
            ],
          }}
          assetColumn={{
            width: [1, 1, 1, 0.58],
            itemsToRender: [
              (props) => (
                <FlexContainer pl={[0, 0, 60, 80]}>
                  <Step1Asset />
                </FlexContainer>
              ),
              (props) => (
                <FlexContainer justifyContent="flex-start" pr={[0, 0, 60, 80]}>
                  <Step2Webp />
                </FlexContainer>
              ),
              (props) => (
                <FlexContainer pl={[0, 0, 60, 80]}>
                  <Step3Asset />
                </FlexContainer>
              ),
            ],
          }}
        />
        <EmbedOnlyModal
          visible={isEmbedModalOpen}
          isClosable
          onChangeVisible={setIsEmbedModalOpen}
        >
          <EmbeddedWebform src={webformUrl} minHeight={700} />
        </EmbedOnlyModal>
      </HowToSection>
    )
  }

  const renderTalkToSales = () => {
    return (
      <TalkToSalesContainer>
        <H2>
          {isUserLoggedInToAnvil
            ? "Don't reinvent the wheel"
            : 'Talk to us about your use case'}
        </H2>
        <p>
          {isUserLoggedInToAnvil ? (
            <>
              Save development time with Anvil's pre-built{' '}
              {shouldRenderPdfTemplateView ? 'PDF templates' : 'Workflows'}.
              Simply add this {title} form template to your account and start
              filling PDFs right away.
            </>
          ) : (
            <>
              Our pre-built Workflows save teams 100s of hours of manual work.
              Tell us how you're trying to use this {title} form into your
              product.
            </>
          )}
        </p>
        <Button
          type="black"
          chevron={{ position: 'inner', direction: 'right' }}
          size="large"
          to={
            isUserLoggedInToAnvil
              ? shouldRenderPdfTemplateView
                ? copyPDFTemplateUrl
                : copyWorkflowUrl
              : FORM_SCROLL_NAME
          }
        >
          {isUserLoggedInToAnvil
            ? 'Add this template'
            : 'Explore your use case'}
        </Button>
      </TalkToSalesContainer>
    )
  }

  const getCompanyType = (tag) => {
    switch (tag) {
      case 'Taxes':
        return 'tax'
      case 'Employment':
        return 'human resources'
      default:
        return tag.toLowerCase()
    }
  }

  const carouselNodes = relatedNodes?.length
    ? relatedNodes
    : allTagArticleNodes?.filter((node) => {
        return node.frontmatter.slug !== slug
      })

  const renderOtherForms = () => {
    return carouselNodes.length ? (
      <OtherFormsContainer>
        <OtherFormsHeader>
          <H2>See other {getCompanyType(selectedTag)} forms</H2>
          <p>
            Explore more {getCompanyType(selectedTag)} PDF forms below or see
            all {getCompanyType(selectedTag)} templates{' '}
            <UniversalLink
              to={`/${FORMS_SLUG}/${kebabCase(selectedTag.toLowerCase())}/`}
            >
              here
            </UniversalLink>
            .
          </p>
        </OtherFormsHeader>
        <StyledCarousel
          slidesToShow={carouselNodes.length >= 3 ? 3 : carouselNodes.length}
          withArrows
          arrowsPosition="bottom-right"
          responsive={[
            {
              breakpoint: pixels.giant,
              settings: {
                slidesToShow: 2,
              },
            },
            {
              breakpoint: pixels.desktop,
              settings: {
                slidesToShow: 1,
              },
            },
          ]}
        >
          {carouselNodes.map(
            ({
              frontmatter: {
                title,
                tagline,
                tags,
                slug,
                pdfTemplateEid,
                workflowEid,
              },
            }) => {
              const otherFormContainsSelectedTag = tags.includes(selectedTag)
              return (
                <SummaryContainer key={title}>
                  <NewFormSummary
                    title={title}
                    description={tagline}
                    tag={selectedTag}
                    to={`/${FORMS_SLUG}/${kebabCase(
                      (otherFormContainsSelectedTag
                        ? selectedTag
                        : tags[0]
                      ).toLowerCase()
                    )}/${slug}/`}
                    isPdfTemplate={pdfTemplateEid && !workflowEid}
                    pdfScreenshot={pdfScreenshot}
                  />
                </SummaryContainer>
              )
            }
          )}
        </StyledCarousel>
      </OtherFormsContainer>
    ) : null
  }

  const renderSolutionsCTA = () => {
    return <StyledSolutionsCTA tag={selectedTag} />
  }

  const renderMainContent = () => {
    const menuItems = possibleFormTags.map((tag) => ({
      key: tag,
      value: tag,
      as: UniversalLink,
      to: `/${FORMS_SLUG}/${kebabCase(tag.toLowerCase())}/`,
      state: { selectedFormTag: tag },
    }))

    return (
      <StyledSidebarSection
        vignette={{
          position: 'top-right',
          size: 'medium',
          color: theme.colors.blues[10],
        }}
        selectedItem={selectedTag}
        sidebarComponent={Sidebar}
        renderHeader={() => {}}
        menuItems={menuItems}
        search={{
          onSearch: ({ query }) => {
            if (query) navigate(`/${FORMS_SLUG}/?q=${query}`)
          },
        }}
      >
        <MainContentContainer>
          {renderHeader()}
          {renderPDFImageSection()}
          {renderHowTo()}
          {renderTalkToSales()}
          {renderOtherForms()}
          {renderSolutionsCTA()}
          <BackToLibraryLink
            to={`/${FORMS_SLUG}/`}
            state={
              locationState?.selectedTag
                ? { selected: locationState.selectedTag }
                : undefined
            }
          >
            Back to Library
          </BackToLibraryLink>
        </MainContentContainer>
      </StyledSidebarSection>
    )
  }

  const renderSignup = () => <ContactAnvil scrollName={FORM_SCROLL_NAME} />

  return (
    <Layout themeId="form">
      <Helmet
        title={title}
        meta={{ description: summary, keywords }}
        pathname={pathname}
        canonicalPathname={getPagePath({
          prefixSlug: FORMS_SLUG,
          tagSlug: kebabCase(canonicalTag),
          pageSlug: slug,
        })}
        socialImageURL={getSocialCoverURL('library', 'forms', {
          additionalPath: `/${slug}`,
        })}
      />
      {renderNavbar()}
      {renderMainContent()}
      {renderSignup()}
    </Layout>
  )
}

export default FormTemplate

FormTemplate.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.shape({
      selectedTag: PropTypes.string,
    }),
  }),
  data: PropTypes.shape({
    site: PropTypes.shape({
      siteMetadata: PropTypes.shape({ siteUrl: PropTypes.string }),
    }),
    form: PropTypes.object,
    relatedForms: PropTypes.shape({ nodes: PropTypes.array }),
  }),
  pageContext: PropTypes.shape({
    newHtmlAst: PropTypes.object,
  }),
}

export const pageQuery = graphql`
  query FreeFormByPath(
    $id: String
    $relatedNodesIds: [String]
    $selectedTag: String
  ) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    form: markdownRemark(id: { eq: $id }) {
      frontmatter {
        slug
        appSlug
        title
        lastUpdated
        workflowEid
        pdfTemplateEid
        tags
        keywords
        summary
        type
      }
      fields {
        dateLastModified
      }
      pdfScreenshot {
        childImageSharp {
          gatsbyImageData
        }
      }
    }
    relatedArticles: allMarkdownRemark(
      limit: 2000
      filter: { id: { in: $relatedNodesIds } }
    ) {
      nodes {
        id
        frontmatter {
          slug
          title
          tags
          tagline
          pdfTemplateEid
          workflowEid
        }
      }
    }
    allTagArticles: allMarkdownRemark(
      limit: 2000
      filter: { frontmatter: { tags: { in: [$selectedTag] } } }
    ) {
      nodes {
        id
        frontmatter {
          slug
          title
          tags
          tagline
          pdfTemplateEid
          workflowEid
        }
      }
    }
  }
`
